import PropTypes from "prop-types";

/**
 * Basic Common PropTypes
 * Reusable PropTypes for fundamental component properties used across the application
 */

/**
 * PropType for component children - any renderable React content
 * Accepts single node, array of nodes, or any React element
 * @type {PropTypes.Requireable<PropTypes.ReactNodeLike>}
 */
export const childrenPropType = PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]);

/**
 * PropType for CSS class name string
 * Used to pass additional CSS classes to components
 * @type {PropTypes.Requireable<string>}
 */
export const classNamePropType = PropTypes.string;

/**
 * PropType for inline style object
 * Used to pass custom inline styles to components
 * @type {PropTypes.Requireable<object>}
 */
export const stylePropType = PropTypes.object;

/**
 * PropType for component ID - can be string or number
 * Used for unique identifiers across components
 * @type {PropTypes.Requireable<string | number>}
 */
export const idPropType = PropTypes.oneOfType([PropTypes.string, PropTypes.number]);

/**
 * PropType for boolean disabled state
 * Used to disable user interaction with components
 * @type {PropTypes.Requireable<boolean>}
 */
export const disabledPropType = PropTypes.bool;

/**
 * PropType for boolean required field indicator
 * Used to mark form fields as required
 * @type {PropTypes.Requireable<boolean>}
 */
export const requiredPropType = PropTypes.bool;

/**
 * PropType for string placeholder text
 * Used in form inputs and search fields
 * @type {PropTypes.Requireable<string>}
 */
export const placeholderPropType = PropTypes.string;

/**
 * PropType for string label text
 * Used for labeling form inputs and components
 * @type {PropTypes.Requireable<string>}
 */
export const labelPropType = PropTypes.string;

/**
 * PropType for string title text
 * Used for component titles and headings
 * @type {PropTypes.Requireable<string>}
 */
export const titlePropType = PropTypes.string;

/**
 * PropType for icon elements - React node or component
 * Accepts any renderable React content typically used for icons
 * @type {PropTypes.Requireable<PropTypes.ReactNodeLike>}
 */
export const iconPropType = PropTypes.node;

/**
 * PropType for string or number values
 * Used for flexible value types that can be displayed or stored
 * @type {PropTypes.Requireable<string | number>}
 */
export const valuePropType = PropTypes.oneOfType([PropTypes.string, PropTypes.number]);

/**
 * PropType for string name attribute
 * Used for form field names and component identifiers
 * @type {PropTypes.Requireable<string>}
 */
export const namePropType = PropTypes.string;

/**
 * PropType for string message text
 * Used for validation messages, error messages, and notifications
 * @type {PropTypes.Requireable<string>}
 */
export const messagePropType = PropTypes.string;
